import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import MailIcon from '@mui/icons-material/Mail';
import PermPhoneMsgIcon from '@mui/icons-material/PermPhoneMsg';
import {
    Alert,
    Badge,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { RevokeContactConsentRequest } from '../../../sic-lib/api/contactConsent/types/ContactConsent';
import { useRevokeConsent } from '../../../sic-lib/api/hooks/useContactConsent';
import { Contact } from '../../../sic-lib/types/Contact';
import AxiosErrorAlert from '../../Shared/Alerts/AxiosErrorAlert';
import ButtonLoading from '../../Shared/ButtonLoading';

interface Props {
    contact: Contact;
    isOpen: boolean;
    setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
}
export default function DialogRevokeConsent({ contact, isOpen, setShowDialog }: Props) {
    const [formState, setFormState] = useState<RevokeContactConsentRequest>({
        contactId: contact.contactId,
        email: contact.consent?.emailConsent || false,
        telephony: contact.consent?.smsConsent || false,
        reason: '',
    });

    const [isDisable, setIsDisable] = useState<boolean>(true);

    const {
        mutate: revokeConsentMutation,
        isLoading,
        isSuccess,
        isError,
        error,
    } = useRevokeConsent(contact.contactId);

    const handleCloseDialog = () => {
        setShowDialog(false);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormState({
            ...formState,
            [event.target.name]: event.target.checked,
        });
    };

    const handleReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormState({
            ...formState,
            reason: event.target.value,
        });
    };

    const handleRevokeConsent = () => {
        try {
            //ATTENTION: Email and Telephony MUST be opposite of the actual state.
            //This is because the backend expects true to revoke consent and false to keep consent.
            revokeConsentMutation({
                ...formState,
                email: !formState.email,
                telephony: !formState.telephony,
            });
            // handleCloseDialog();
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        if (contact.consent?.emailConsent && contact.consent?.smsConsent) {
            if (!formState.email || !formState.telephony) setIsDisable(false);
        } else if (
            (contact.consent?.emailConsent || contact.consent?.smsConsent) &&
            !formState.email &&
            !formState.telephony
        ) {
            setIsDisable(false);
        } else {
            setIsDisable(true);
        }

        return () => {
            setIsDisable(true);
        };
    }, [
        formState.email,
        formState.telephony,
        contact.consent?.emailConsent,
        contact.consent?.smsConsent,
    ]);

    console.log('isDisable', isDisable);
    return (
        <Dialog open={isOpen} onClose={handleCloseDialog}>
            <DialogTitle>Revoke Consent</DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <Alert severity="warning">
                        Proceeding is a final action and cannot be undone. You will not be able to
                        reapply consent to the contact afterward.
                    </Alert>
                    <Alert severity="info">
                        Turn off the consent switches to revoke the consent.
                    </Alert>
                </Stack>
                <Stack direction="column" spacing={2} mt={4}>
                    <Stack direction="row" spacing={2} justifyContent="space-between">
                        <Stack
                            direction="row"
                            spacing={2}
                            justifyContent="flex-start"
                            alignItems="center"
                        >
                            <Tooltip title="Email Consent">
                                <Badge
                                    badgeContent={
                                        contact.consent?.emailConsent ? (
                                            <CheckIcon fontSize="small" />
                                        ) : (
                                            <CloseIcon fontSize="small" />
                                        )
                                    }
                                    color={contact.consent?.emailConsent ? 'success' : 'error'}
                                    sx={{
                                        '.MuiBadge-badge': {
                                            padding: 0,
                                            fontSize: '1.5rem',
                                        },
                                    }}
                                >
                                    <MailIcon color="action" fontSize="large" />
                                </Badge>
                            </Tooltip>
                            <Typography variant="body2" textAlign="center">
                                Email Consent
                            </Typography>
                        </Stack>
                        <Stack width="50%">
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formState.email}
                                            onChange={handleChange}
                                            name="email"
                                            disabled={!contact.consent?.emailConsent}
                                        />
                                    }
                                    label={
                                        formState.email
                                            ? 'email consent allowed'
                                            : !contact.consent?.emailConsent
                                            ? 'email consent revoked'
                                            : 'revoke email consent'
                                    }
                                    slotProps={{
                                        typography: {
                                            color: !formState.email ? 'error' : '',
                                        },
                                    }}
                                />
                            </FormGroup>
                        </Stack>
                    </Stack>
                    <Stack direction="row" spacing={2} justifyContent="space-between">
                        <Stack
                            direction="row"
                            spacing={2}
                            justifyContent="flex-start"
                            alignItems="center"
                        >
                            <Tooltip title="Telephony Consent">
                                <Badge
                                    badgeContent={
                                        contact.consent?.smsConsent ? (
                                            <CheckIcon fontSize="small" />
                                        ) : (
                                            <CloseIcon fontSize="small" />
                                        )
                                    }
                                    color={contact.consent?.smsConsent ? 'success' : 'error'}
                                    sx={{
                                        '.MuiBadge-badge': {
                                            padding: 0,
                                        },
                                    }}
                                >
                                    <PermPhoneMsgIcon color="action" fontSize="large" />
                                </Badge>
                            </Tooltip>
                            <Typography variant="body2" textAlign="center">
                                Telephony Consent
                            </Typography>
                        </Stack>
                        <Stack width="50%">
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formState.telephony}
                                            onChange={handleChange}
                                            name="telephony"
                                            disabled={!contact.consent?.smsConsent}
                                        />
                                    }
                                    label={
                                        formState.telephony
                                            ? 'telephony consent allowed'
                                            : !contact.consent?.smsConsent
                                            ? 'telephony consent revoked'
                                            : 'revoke telephony consent'
                                    }
                                    slotProps={{
                                        typography: {
                                            color: !formState.telephony ? 'error' : '',
                                        },
                                    }}
                                />
                            </FormGroup>
                        </Stack>
                    </Stack>
                    {!isDisable && (
                        <Stack display="flex" flexGrow={1}>
                            <TextField
                                label="Revoke consent reasons"
                                multiline
                                value={formState.reason}
                                onChange={handleReasonChange}
                                rows={4}
                                fullWidth
                            />
                        </Stack>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Stack direction="column" spacing={2}>
                    <Stack direction="row" spacing={2}>
                        <Button onClick={handleCloseDialog}>Cancel</Button>
                        <ButtonLoading
                            isLoading={isLoading}
                            isSuccess={isSuccess}
                            isError={isError}
                            successLabel="Consent Revoked"
                            variant="contained"
                            color="error"
                            onClick={handleRevokeConsent}
                            disabled={isDisable}
                        >
                            Apply Consent Changes
                        </ButtonLoading>
                    </Stack>
                    <AxiosErrorAlert error={error} />
                </Stack>
            </DialogActions>
        </Dialog>
    );
}
